import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider, CssBaseline, GlobalStyles } from "@mui/material";
import { buildAppTheme, Mode } from "./appTheme";

const STORAGE_KEY = "themeMode";

type ColorModeValue = {
  mode: Mode;
  toggleMode: () => void;
};

const ColorModeContext = createContext<ColorModeValue | null>(null);

export const useColorMode = (): ColorModeValue => {
  const ctx = useContext(ColorModeContext);
  if (!ctx) throw new Error("useColorMode must be used within ColorModeProvider");
  return ctx;
};

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Synchronous read so the first paint is already in the saved mode (no flash)
  const [mode, setMode] = useState<Mode>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
    } catch {
      return "light";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* storage unavailable (private mode) — mode just won't persist */
    }
  }, [mode]);

  const toggleMode = useCallback(() => setMode((m) => (m === "light" ? "dark" : "light")), []);
  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);
  const theme = useMemo(() => buildAppTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            // Scoped crossfade on mode switch — never `*` (kills chart/map perf)
            body: { transition: "background-color .3s ease, color .3s ease" },
            ".MuiAppBar-root": { transition: "background-color .3s ease, color .3s ease, border-color .3s ease" },
            ".MuiDrawer-paper": { transition: "background-color .3s ease, color .3s ease, border-color .3s ease" },
          }}
        />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
